import type { Ctx } from '@milkdown/kit/ctx';
import { uploadConfig } from '@milkdown/kit/plugin/upload';
import type { Node, Schema } from '@milkdown/kit/prose/model';
import { Decoration } from '@milkdown/kit/prose/view';

/** Read a File as a base64 data URL. */
export function fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error(reader.error?.message ?? 'Failed to read file'));
        reader.readAsDataURL(file);
    });
}

/**
 * Custom uploader for Milkdown's upload plugin.
 * Converts images to base64 data URIs (no server upload needed).
 */
export async function imageUploader(
    files: FileList,
    schema: Schema,
    _ctx: Ctx,
    _insertPos: number
): Promise<Node[]> {
    const images: File[] = [];
    for (let i = 0; i < files.length; i++) {
        const file = files.item(i);
        if (file?.type.startsWith('image/')) {
            images.push(file);
        }
    }

    const nodes = await Promise.all(
        images.map(async (image) => {
            const dataUrl = await fileToDataUrl(image);

            return schema.nodes.image.createAndFill({
                src: dataUrl,
                alt: image.name
            })!;
        })
    );

    return nodes;
}

/**
 * Configures the upload plugin with our custom uploader.
 */
export function configureImageUpload(ctx: Ctx) {
    ctx.set(uploadConfig.key, {
        uploader: imageUploader,
        enableHtmlFileUploader: false,
        uploadWidgetFactory: (pos, spec) => Decoration.widget(pos, document.createElement('span'), spec)
    });
}
